import React from 'react';
import { TrendingUp, TrendingDown, Star, Award } from 'lucide-react';

interface TopPerformer {
  symbol: string;
  name: string;
  upside: number;
  rank: number;
  sector?: string;
}

interface TopPerformersProps {
  gainers: TopPerformer[];
  losers: TopPerformer[];
}

export const TopPerformers: React.FC<TopPerformersProps> = ({ gainers, losers }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top Gainers */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl shadow-xl border-2 border-green-200 p-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-400/10 rounded-full -mr-32 -mt-32" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Top Gainers</h3>
              <p className="text-sm text-green-700">Highest potential upside</p>
            </div>
          </div>

          <div className="space-y-3">
            {gainers.map((stock) => (
              <div
                key={stock.rank}
                className="group bg-white hover:bg-green-50 rounded-xl p-4 border border-green-200 hover:border-green-400 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                        <span className="text-white font-bold text-sm">#{stock.rank}</span>
                      </div>
                      {stock.rank === 1 && (
                        <div className="absolute -top-1 -right-1">
                          <Award className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-base">{stock.symbol}</span>
                      {stock.sector && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          {stock.sector}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{stock.name}</p>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-lg font-bold text-green-600">
                      <TrendingUp className="h-5 w-5" />
                      +{stock.upside.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Losers */}
      <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl shadow-xl border-2 border-red-200 p-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-400/10 rounded-full -mr-32 -mt-32" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl shadow-lg">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Top Losers</h3>
              <p className="text-sm text-red-700">Highest potential downside</p>
            </div>
          </div>

          <div className="space-y-3">
            {losers.map((stock) => (
              <div
                key={stock.rank}
                className="group bg-white hover:bg-red-50 rounded-xl p-4 border border-red-200 hover:border-red-400 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-white font-bold text-sm">#{stock.rank}</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-base">{stock.symbol}</span>
                      {stock.sector && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          {stock.sector}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{stock.name}</p>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-lg font-bold text-red-600">
                      <TrendingDown className="h-5 w-5" />
                      {stock.upside.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
